import { Coupon as CouponModel } from '@models/membership/Coupon'
import { Subscription as SubscriptionModel } from '@models/membership/Subscription'
import { attachCouponToSubscription } from '@services/membership/SubscriptionService'
import {
  SBAsyncSelect,
  SBAsyncSelectProps,
} from '@supplement-bacon/react-async-select'
import { initialQuery } from '@supplement-bacon/sb-table-filters'
import { Entity } from '@utils/Entities'
import { requiredRule } from '@utils/rules'
import { Button, Form, Modal } from 'antd'
import { FC, useState } from 'react'

type Props = {
  subscription: SubscriptionModel
  onChange?: () => void | Promise<unknown>
}

/**
 * Attaches a coupon to an existing subscription.
 *
 * Built for the legacy "Have to pay = false" members: attaching a full-amount,
 * non-first-cycle-only coupon makes every renewal cycle resolve to 0, so the
 * renewal cron renews them for free instead of failing on a missing card.
 * Coupons are filtered to those linked to the subscription's plan; the API
 * re-validates that link and never increments the coupon's usage.
 *
 * Refresh goes through `onChange` (the parent re-fetches the membership
 * profile, which reloads the subscription with its coupon) rather than
 * re-fetching the subscription directly — the latter re-normalises the
 * subscription's nested profiles without their `subscription` link and
 * unmounts the card.
 */
const AttachCoupon: FC<Props> = ({ subscription, onChange }) => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()

  const planId = subscription.plan?.id

  const onSubmit = async () => {
    const { coupon } = await form.validateFields()
    setLoading(true)
    try {
      await attachCouponToSubscription(subscription.id, coupon)
      await onChange?.()
      setOpen(false)
      form.resetFields()
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Button size="small" onClick={() => setOpen(true)}>
        {subscription.coupon ? 'Change coupon' : 'Attach coupon'}
      </Button>
      <Modal
        title="Attach a coupon"
        open={open}
        onOk={onSubmit}
        confirmLoading={loading}
        onCancel={() => setOpen(false)}
        okText="Attach"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="coupon" label="Coupon" rules={[requiredRule()]}>
            <SBAsyncSelect<CouponModel>
              entity={Entity.coupon}
              searchFieldName="code"
              initialQuery={
                {
                  ...initialQuery,
                  filters: planId ? { plans: [planId] } : {},
                } as SBAsyncSelectProps<CouponModel>['initialQuery']
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default AttachCoupon
